export interface ImpositionResult {
  outs: number;
  layoutType: "VERTICAL" | "HORIZONTAL" | "NONE";
  usableArea: number;
  wastePct: number;
  cols: number;
  rows: number;
  orientation: "PORTRAIT" | "LANDSCAPE";
  printableW: number;
  printableL: number;
  neededW: number;
  neededL: number;
  cutsCount: number;
}

export interface StandardSheetPreset {
  name: string;
  width: number;
  length: number;
}

export const STANDARD_OFFSET_SHEETS: StandardSheetPreset[] = [
  { name: "Khổ 65 x 86 cm", width: 650, length: 860 },
  { name: "Khổ 79 x 109 cm", width: 790, length: 1090 },
  { name: "Khổ 60 x 84 cm", width: 600, length: 840 },
  { name: "Khổ 54 x 79 cm", width: 540, length: 790 },
  { name: "Khổ 43 x 65 cm", width: 430, length: 650 },
  { name: "Khổ 39.5 x 54.5 cm", width: 395, length: 545 },
  { name: "Khổ 32 x 43 cm", width: 320, length: 430 },
];

/**
 * Thuật toán tính toán bình file offset (Imposition)
 * @param sheetW Chiều rộng tờ in (mm)
 * @param sheetL Chiều dài tờ in (mm)
 * @param flatW Chiều rộng khổ trải sản phẩm (mm)
 * @param flatL Chiều dài khổ trải sản phẩm (mm)
 * @param gripper Lề nhíp (mm) - mặc định 15mm
 * @param bleed Lề chừa xén (mm) - mặc định 3mm
 * @param sideMargins Lề biên hai bên và đuôi (mm) - mặc định 5mm
 */
export function calculateImposition(
  sheetW: number,
  sheetL: number,
  flatW: number,
  flatL: number,
  gripper: number = 15,
  bleed: number = 3,
  sideMargins: number = 5
): ImpositionResult {
  if (sheetW <= 0 || sheetL <= 0 || flatW <= 0 || flatL <= 0) {
    return {
      outs: 0,
      layoutType: "NONE",
      usableArea: 0,
      wastePct: 100,
      cols: 0,
      rows: 0,
      orientation: "PORTRAIT",
      printableW: 0,
      printableL: 0,
      neededW: 0,
      neededL: 0,
      cutsCount: 0,
    };
  }

  // Vùng có thể in hiệu dụng sau khi trừ lề nhíp và lề biên kỹ thuật
  const printableW = Math.max(0, sheetW - sideMargins * 2);
  const printableL = Math.max(0, sheetL - gripper - sideMargins);

  // Khổ sản phẩm thực tế kèm bleed chừa xén
  const neededW = flatW + bleed * 2;
  const neededL = flatL + bleed * 2;

  if (neededW <= 0 || neededL <= 0) {
    return {
      outs: 0,
      layoutType: "NONE",
      usableArea: 0,
      wastePct: 100,
      cols: 0,
      rows: 0,
      orientation: "PORTRAIT",
      printableW,
      printableL,
      neededW,
      neededL,
      cutsCount: 0,
    };
  }

  // Cách 1: Bình dọc (0 độ - Sản phẩm giữ nguyên hướng)
  const colsVertical = Math.floor(printableW / neededW);
  const rowsVertical = Math.floor(printableL / neededL);
  const outsVertical = Math.max(0, colsVertical * rowsVertical);

  // Cách 2: Bình ngang (90 độ - Xoay ngang sản phẩm)
  const colsHorizontal = Math.floor(printableW / neededL);
  const rowsHorizontal = Math.floor(printableL / neededW);
  const outsHorizontal = Math.max(0, colsHorizontal * rowsHorizontal);

  // Lựa chọn cách bố trí tối ưu nhất (nhiều con nhất)
  let outs = 0;
  let layoutType: "VERTICAL" | "HORIZONTAL" | "NONE" = "VERTICAL";
  let cols = 0;
  let rows = 0;

  if (outsHorizontal > outsVertical) {
    outs = outsHorizontal;
    layoutType = "HORIZONTAL";
    cols = colsHorizontal;
    rows = rowsHorizontal;
  } else {
    outs = outsVertical;
    layoutType = "VERTICAL";
    cols = colsVertical;
    rows = rowsVertical;
  }

  if (outs <= 0) {
    return {
      outs: 0,
      layoutType: "NONE",
      usableArea: 0,
      wastePct: 100,
      cols: 0,
      rows: 0,
      orientation: sheetW > sheetL ? "LANDSCAPE" : "PORTRAIT",
      printableW,
      printableL,
      neededW,
      neededL,
      cutsCount: 0,
    };
  }

  const sheetArea = sheetW * sheetL;
  const usedArea = outs * (flatW * flatL);
  const wasteFraction = sheetArea > 0 ? (1 - usedArea / sheetArea) : 1;
  const wastePct = Math.max(0, Math.min(100, Math.round(wasteFraction * 100)));
  const cutsCount = (cols > 0 && rows > 0) ? (cols - 1) + (rows - 1) + 4 : 0;

  return {
    outs,
    layoutType,
    usableArea: Math.round(usedArea),
    wastePct,
    cols,
    rows,
    orientation: sheetW > sheetL ? "LANDSCAPE" : "PORTRAIT",
    printableW,
    printableL,
    neededW,
    neededL,
    cutsCount,
  };
}

/**
 * Gợi ý khổ giấy in tối ưu nhất từ danh sách khổ chuẩn
 */
export function suggestOptimalSheetSize(
  flatW: number,
  flatL: number,
  gripper: number = 15,
  bleed: number = 3,
  presets: StandardSheetPreset[] = STANDARD_OFFSET_SHEETS
) {
  const ranked = presets
    .map(preset => {
      const imp = calculateImposition(preset.width, preset.length, flatW, flatL, gripper, bleed);
      return {
        ...preset,
        imposition: imp,
      };
    })
    .filter(item => item.imposition.outs > 0)
    .sort((a, b) => {
      // Ưu tiên: Nhiều con hơn trước, nếu bằng nhau thì chọn khổ ít hao hụt hơn
      if (b.imposition.outs !== a.imposition.outs) {
        return b.imposition.outs - a.imposition.outs;
      }
      return a.imposition.wastePct - b.imposition.wastePct;
    });

  return ranked;
}

